// UI wording for the AI-modification screens (SOW Deliverable 4).
//
// DRAFT, FOR VANJA. Every line here is plain UI wording drafted by the build
// (decision 5 of the phase-1 plan), kept in its own file so nothing in
// strings.constant.ts changes. NO LEGAL TEXT lives here: the modification
// terms come from the server (E1) and the contract from the server (E9).
// NO RULE lives here either: the statuses, their labels, the rejection limit,
// the categories, the file types and the byte limits all come from the
// server's answers. The failure-reason sentences below are wording for codes
// the server sends; an unknown code shows the default sentence.

// ---------- Shared ----------

export const MOD_NOT_AVAILABLE_TITLE_TEXT = 'AI modifications';
export const MOD_NOT_AVAILABLE_TEXT = 'AI modifications are not available right now.';
export const MOD_LOADING_TEXT = 'Loading...';
export const MOD_ERROR_AUTH_TEXT = 'Please sign in again.';
export const MOD_ERROR_FORBIDDEN_TEXT = 'You do not have access to this.';
export const MOD_ERROR_NOT_FOUND_TEXT = 'Not found.';
export const MOD_ERROR_CONFLICT_TEXT = 'This changed before your action arrived. Please check it and try again.';
export const MOD_ERROR_INVALID_TEXT = 'Please check the form and try again.';
export const MOD_ERROR_RETRY_TEXT = 'Something went wrong. Please try again.';
export const MOD_RETRY_TEXT = 'Try again';
export const MOD_VIEW_DETAILS_TEXT = 'View details';
export const MOD_BACK_TO_LIST_TEXT = 'Back to the list';
export const MOD_REJECTIONS_TEXT = (count: number, limit: number) => `Rejections: ${count} of ${limit}`;
export const MOD_REVISION_TEXT = (revision: number) => `Version ${revision}`;
export const MOD_ORDER_TEXT = 'Order';
export const MOD_ORDER_REVOKED_TEXT = 'The licence for this order has ended.';

/** Labels for the server's action codes. An unknown code is shown humanised. */
export const MOD_ACTION_LABELS: Readonly<Record<string, string>> = Object.freeze({
  issue_slot: 'Upload a file',
  submit: 'Finish submitting',
  next_revision: 'Upload a new version',
  accept: 'Accept',
  request_changes: 'Request changes',
  reject: 'Reject',
  lock_reset: 'Reset the lock',
});

/**
 * Plain sentences for the fixed verification reason codes the server sends
 * (modification.constant.ts, VERIFICATION_REASONS). Wording, not rules: an
 * unknown or missing code shows the default.
 */
export const MOD_FAILURE_DEFAULT_TEXT = 'The file could not be accepted. Please upload it again.';
export const MOD_FAILURE_SENTENCES: Readonly<Record<string, string>> = Object.freeze({
  object_missing: 'The upload did not arrive. Please upload the file again.',
  size_mismatch: 'The file that arrived is not the size that was declared. Please upload it again.',
  type_mismatch: 'The file type does not match. Please upload it again.',
  too_large: 'The file is larger than allowed. Please upload a smaller file.',
  not_a_video: 'The file could not be read as a video. Please check it and upload it again.',
  probe_timeout: 'The file took too long to check. Please upload it again.',
  copy_failed: 'The file could not be stored. Please upload it again.',
  enqueue_failed: 'The check could not start. Please upload the file again.',
  order_revoked: 'The licence for this order has ended, so the file cannot be checked.',
  verification_stalled: 'The check did not finish. Please upload the file again.',
});

/**
 * Plain sentences for the fixed reason codes the server puts on an event
 * (modification.constant.ts: "superseded" on slot_abandoned). A reason a
 * person typed, such as a lock reset's, is shown as written.
 */
export const MOD_EVENT_REASON_SENTENCES: Readonly<Record<string, string>> = Object.freeze({
  superseded: 'A newer upload link replaced this one.',
});

export const failureSentence = (code: string | null | undefined): string =>
  (code && Object.prototype.hasOwnProperty.call(MOD_FAILURE_SENTENCES, code) ? MOD_FAILURE_SENTENCES[code] : null) ??
  MOD_FAILURE_DEFAULT_TEXT;

/** A server code shown as words: "background_treatment" becomes "Background treatment". */
export const humanizeCode = (code: string): string => {
  const words = code.replace(/_/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : code;
};

// ---------- Order page panel ----------

export const MOD_ORDER_PANEL_TITLE_TEXT = 'AI modification';
export const MOD_SUBMIT_BUTTON_TEXT = 'Submit AI modification';

// ---------- Upload and terms ----------

export const MOD_UPLOAD_TITLE_TEXT = 'Submit an AI modification';
export const MOD_UPLOAD_REVISION_TITLE_TEXT = 'Upload a new version';
export const MOD_FILE_STEP_TEXT = 'Your modified video';
export const MOD_FILE_LIMITS_TEXT = (types: string, size: string) => `Accepted: ${types}. Up to ${size}.`;
/** The dropzone's hint: the types E1 allows, as the server sent them. */
export const MOD_DROPZONE_HINT_TEXT = (types: string) => `or click to browse · ${types}`;
export const MOD_FILE_REFUSED_TEXT = 'This file cannot be used. Please choose a video of an accepted type, within the size limit.';
export const MOD_FILE_CHOSEN_TEXT = (name: string) => `Chosen: ${name}`;
export const MOD_DETAILS_STEP_TEXT = 'About the change';
export const MOD_DESCRIPTION_LABEL = 'What did you change?';
export const MOD_CATEGORY_LABEL = 'Kind of change';
export const MOD_TOOLS_LABEL = 'AI tools used (optional)';
export const MOD_TERMS_STEP_TEXT = 'Modification terms';
export const MOD_TERMS_VERSION_TEXT = (version: number) => `Terms version ${version}`;
export const MOD_TERMS_ACCEPT_TEXT = 'I have read and accept these terms';
export const MOD_TYPED_NAME_LABEL = 'Type your full name to sign';
export const MOD_SEND_TEXT = 'Submit for review';
export const MOD_UPLOADING_TEXT = 'Uploading...';
export const MOD_CHECKING_TEXT = 'Checking your file. This can take a few minutes.';
export const MOD_STILL_CHECKING_TEXT = 'The check is still running. You can leave this page; the result will show on the modification page.';
export const MOD_RESULT_TITLE_TEXT = 'Submitted';
export const MOD_RESULT_REFUSED_TITLE_TEXT = 'The file was not accepted';
export const MOD_TERMS_CHANGED_TEXT = 'The terms have changed. Please read the new version and accept it again.';
export const MOD_CHARS_LEFT_TEXT = (left: number) => `${left} characters left`;

// ---------- Lists ----------

export const MOD_MY_TITLE_TEXT = 'My AI modifications';
export const MOD_REVIEW_TITLE_TEXT = 'AI modifications to review';
export const MOD_COL_TITLE_TEXT = 'Video';
export const MOD_COL_ORDER_TEXT = 'Order';
export const MOD_COL_STATUS_TEXT = 'Status';
export const MOD_COL_UPDATED_TEXT = 'Updated';
export const MOD_FILTER_LABEL_TEXT = 'Show';
export const MOD_FILTER_ALL_TEXT = 'All';
export const MOD_EMPTY_TEXT = 'Nothing here yet.';

// ---------- Detail and history ----------

export const MOD_DETAIL_TITLE_TEXT = 'AI modification';
export const MOD_REVISIONS_TITLE_TEXT = 'Versions';
export const MOD_WATCH_TEXT = (revision: number) => `Watch version ${revision}`;
export const MOD_HISTORY_TITLE_TEXT = 'History';
export const MOD_HISTORY_COMMENT_TEXT = 'Comment';
export const MOD_HISTORY_REASON_TEXT = 'Reason';
export const MOD_LAST_FAILURE_TEXT = 'Last upload';
export const MOD_DECLARED_TITLE_TEXT = 'What the buyer declared';
export const MOD_DECLARED_FILE_TEXT = 'File';
export const MOD_DECLARED_CATEGORY_TEXT = 'Kind of change';
export const MOD_DECLARED_DESCRIPTION_TEXT = 'What changed';
export const MOD_DECLARED_TOOLS_TEXT = 'AI tools used';

// ---------- Contract ----------

export const MOD_CONTRACT_TITLE_TEXT = 'Modification contract';
export const MOD_CONTRACT_VIEW_TEXT = 'View';
export const MOD_CONTRACT_DOWNLOAD_TEXT = 'Download';
export const MOD_CONTRACT_HASH_TEXT = 'SHA-256';
export const MOD_CONTRACT_ORDER_STATUS_TEXT = 'Order status';
export const MOD_CONTRACT_FILE_NAME = (id: string) => `modification-contract-${id}.pdf`;
export const MOD_CONTRACT_LOADING_TEXT = 'Preparing the contract...';
export const MOD_CONTRACT_FAILED_TEXT = 'The contract could not be loaded.';

// The accepted-agreement panel (A3, Joey 26 Oct): the same agreement is recorded with the request and again with
// each version, so each Download says which it is. DRAFT: approved by Joey for dev; needs Vanja's OK before UAT and
// prod. "DRAFT" is in this comment only, never on screen.
export const MOD_ACCEPTANCES_INTRO_TEXT = 'This agreement is recorded with the request and again with each version uploaded under it.';
export const MOD_ACCEPTED_WITH_REQUEST_TEXT = 'Accepted with the request';
export const MOD_RECORDED_WITH_VERSION_TEXT = (revision: number) => `Recorded with version ${revision}`;

// ---------- Review ----------

export const MOD_REVIEW_DETAIL_TITLE_TEXT = 'Review an AI modification';
export const MOD_SOURCE_CLIP_TITLE_TEXT = 'Licensed clip';
export const MOD_MODIFIED_CLIP_TITLE_TEXT = 'Modified version';
export const MOD_LICENSED_TIMES_TEXT = (start: string, end: string) => `Licensed from ${start} to ${end}`;
// The admin video manager (2026-10-26): a Platform Admin removed or archived the source video, so the gateway answers
// its page with "not found". The review goes on with the modified clip.
export const MOD_SOURCE_REMOVED_TEXT = 'The original video has been removed.';
export const MOD_COMMENT_LABEL = 'Comment for the buyer';
export const MOD_COMMENT_HINT_TEXT = 'A comment is required to reject or request changes.';
export const MOD_DECISION_SENT_TEXT = 'Your decision was saved.';
export const MOD_REASON_LABEL = 'Reason for the reset';
export const MOD_RESET_SENT_TEXT = 'The lock was reset.';

// ---------- Admin ----------

export const MOD_ADMIN_SECTION_LABEL = 'AI modifications';
export const MOD_ADMIN_LOCKED_TITLE_TEXT = 'Locked';
export const MOD_ADMIN_STALE_TITLE_TEXT = 'Waiting for review too long';
export const MOD_ADMIN_PENDING_SINCE_TEXT = 'Waiting since';
export const MOD_ADMIN_OWNER_TEXT = 'Licensor';

// ---------- Ways in ----------

export const MOD_PROFILE_ENTRY_TEXT = 'AI modifications';
export const MOD_REVIEW_NAV_LABEL = 'Review AI modifications';
